/**
 * The "Yeni Çekim" split menu.
 *
 * Four items. The first three name what will be uploaded and *carry* that
 * choice into the form, which is new: they used to be three labels wired to
 * one handler, so picking "Video Yükle" still opened a form defaulting to
 * photo. A menu item that does not do what it says is worse than not having
 * it.
 *
 * "Fotoğraf + Video" exists because a real capture is usually both - a video
 * pass for coverage and stills for detail - and until now that session type
 * could only be reached by opening the wrong menu item and correcting a
 * dropdown. The difference is that
 * "Telefon ile Çekim" is disabled - with a reason - when the server reports
 * that pairing is not configured, rather than opening a dialog that can only
 * fail. And it says "Telefon ile Çekim", not "Telefon ile Canlı Çekim": the
 * phone can pair, and its camera cannot be used until there is an HTTPS
 * origin, so the word "canlı" would be a promise.
 */

import { useEffect, useRef } from "react";
import { IconCamera, IconChevronDown, IconLayers, IconPhone, IconVideo } from "@/components/icons";
import { Button } from "@/components/ui";
import type { CaptureType } from "@/api/types";
import "./sessions.css";

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Receives the capture type the chosen item names, not just "something". */
  onUpload: (captureType: CaptureType) => void;
  onPhone: () => void;
  pairingUsable: boolean;
}

export function NewCaptureMenu({ open, setOpen, onUpload, onPhone, pairingUsable }: Props) {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open, setOpen]);

  return (
    <div className="capture-menu" ref={root}>
      <Button variant="primary" aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(!open)}>
        Yeni Çekim <IconChevronDown size={16} />
      </Button>
      {open && (
        <div className="capture-menu__panel" role="menu">
          <button
            type="button"
            role="menuitem"
            className="capture-menu__item"
            onClick={() => onUpload("photo")}
          >
            <IconCamera size={18} /> Fotoğraf Yükle
          </button>
          <button
            type="button"
            role="menuitem"
            className="capture-menu__item"
            onClick={() => onUpload("video")}
          >
            <IconVideo size={18} /> Video Yükle
          </button>
          <button
            type="button"
            role="menuitem"
            className="capture-menu__item"
            onClick={() => onUpload("mixed")}
          >
            <IconLayers size={18} />
            <span>
              Fotoğraf + Video
              <span className="capture-menu__note">Karışık çekim</span>
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="capture-menu__item"
            onClick={onPhone}
            disabled={!pairingUsable}
          >
            <IconPhone size={18} />
            <span>
              Telefon ile Çekim
              {!pairingUsable && (
                <span className="capture-menu__note">Yapılandırılmadı</span>
              )}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
